const characters = [
  {
    name: "GUMBO",
    flaw: "Thick eyebrows",
    story: "Found in a knitwear factory reject bin",
    img: "https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/4b4bcb13033b95fa0a5fb6ccc5e49972.png",
    color: "#E63946",
    objectFit: "object-cover",
  },
  {
    name: "MOSSY",
    flaw: "Big Beard",
    story: "Rescued from a print sample discard",
    img: "https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/056f50f6309b01eab69fb688c96acec6.png",
    color: "#1D3557",
    objectFit: "object-cover",
  },
  {
    name: "BRUMBY",
    flaw: "Big Mouth",
    story: "Cut from a denim production overage",
    img: "https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/c53abe8ede111b57d6074aefc2b1768a.png",
    color: "#E63946",
    objectFit: "object-cover",
  },
  {
    name: "WOBBLE",
    flaw: "Long Nose",
    story: "From a skirt that was off-grain",
    img: "https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/723c33be40714264e708d7f3090ac4ae.png",
    color: "#1D3557",
    objectFit: "object-contain",
  },
];

export default function FromFlawSection() {
  return (
    <section id="characters" className="bg-[#F5F3EF] py-24 md:py-36">
      <div className="w-full px-8 md:px-16">

        {/* Header */}
        <div className="flex items-center gap-4 mb-16">
          <span className="bg-[#E63946] text-white font-mono text-[10px] uppercase tracking-[3px] px-3 py-1.5 rounded-full whitespace-nowrap">
            04 / 06
          </span>
          <div className="flex-1 h-[1px] bg-[#0D0D0D]/15" />
          <span className="font-mono text-[10px] text-[#0D0D0D]/30 uppercase tracking-[2px] whitespace-nowrap">
            From Flaw to Character
          </span>
        </div>

        {/* Title and Intro */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
          <div className="md:col-span-6">
            <h2
              className="font-display leading-tight"
              style={{ fontSize: "clamp(52px, 7.5vw, 108px)" }}
            >
              <span className="text-[#E63946]">FLAW</span><br />
              <span className="text-[#0D0D0D]">TO</span><br />
              <span className="text-[#1D3557]">CHARACTER</span>
            </h2>
          </div>
          <div className="md:col-span-6">
            {/* Feature Detail Box */}
            <div className="bg-[#E8E3D8] rounded-3xl p-8 relative overflow-hidden">
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 rounded-full bg-[#E63946] mt-2 flex-shrink-0" />
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[3px] text-[#E63946] mb-2">Design Method</p>
                  <p className="font-body text-[#2B2B2B] text-base leading-relaxed">
                    Each character begins with the flaw itself. The imperfection is not hidden — it is the starting point, the defining feature that gives the character its name, its personality, and its story.
                  </p>
                  <div className="mt-6 grid grid-cols-3 gap-3">
                    {[
                      { label: "Mismatched button", arrow: "↗" },
                      { label: "Frayed edge", arrow: "↙" },
                      { label: "Stain becomes pattern", arrow: "↘" },
                    ].map((note) => (
                      <div key={note.label} className="text-center">
                        <span className="font-display text-xl text-[#E63946]">{note.arrow}</span>
                        <p className="font-mono text-[8px] text-[#2B2B2B]/60 uppercase tracking-[1px] leading-tight mt-1">
                          {note.label}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <p className="font-body text-[#2B2B2B]/60 text-base leading-relaxed mt-6">
              Four characters. Four flaws. Four textile waste stories transformed into collectible bag charms — each one a wearable conversation about imperfection, process, and the beauty of the discarded.
            </p>
          </div>
        </div>

        {/* Character Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
          {characters.map((char) => (
            <div
              key={char.name}
              className="group bg-white rounded-3xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-[#0D0D0D]/10"
            >
              {/* Image */}
              <div className="relative overflow-hidden" style={{ aspectRatio: "1/1" }}>
                <img
                  src={char.img}
                  alt={char.name}
                  className={`w-full h-full ${char.objectFit} object-center group-hover:scale-105 transition-transform duration-500`}
                />
                {/* Hover overlay */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                  style={{ backgroundColor: char.color }}
                />
              </div>

              {/* Info */}
              <div className="p-5 border-t-2" style={{ borderColor: char.color }}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-display text-2xl text-[#0D0D0D] tracking-wider">
                    {char.name}
                  </h3>
                  <span
                    className="font-mono text-[9px] uppercase tracking-[2px] px-2 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: char.color }}
                  >
                    {char.flaw}
                  </span>
                </div>
                <p className="font-body text-[#2B2B2B]/50 text-xs leading-relaxed">
                  {char.story}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Strip */}
        <div className="mt-16 py-5 border-t border-b border-[#0D0D0D]/10 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[4px] text-[#0D0D0D]/30">
            Each character tells a story of transformation
          </p>
        </div>

        {/* Interaction Game Link */}
        <div className="mt-10 flex flex-col items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-[3px] text-[#0D0D0D]/25">
            Interaction game
          </span>
          <div className="flex items-center gap-3">
            <a
              href="https://gemini.google.com/share/12e129275f3c"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="group relative inline-block cursor-pointer"
            >
              <span
                className="font-display text-[#E63946]/60 group-hover:text-[#E63946] transition-colors duration-300"
                style={{
                  fontSize: "clamp(18px, 2vw, 26px)",
                  fontStyle: "italic",
                  letterSpacing: "0.04em",
                }}
              >
                ✦ &nbsp;Meet the Characters&nbsp; ✦
              </span>
              <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 h-[1px] w-0 group-hover:w-full bg-[#E63946]/50 transition-all duration-500 rounded-full" />
            </a>

            {/* Cursor + click me */}
            <div className="flex items-end gap-1.5 select-none" style={{ transform: "translateY(2px)" }}>
              <svg
                width="28"
                height="32"
                viewBox="0 0 18 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-[#0D0D0D]/55"
              >
                <path
                  d="M2 1L2 14L5.5 10.5L7.5 16L9.5 15.2L7.5 9.5L12 9.5L2 1Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="font-display text-[#0D0D0D]/50"
                style={{ fontSize: "16px", fontStyle: "italic", letterSpacing: "0.03em" }}
              >
                click me
              </span>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
